import React, { useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery } from 'react-query';
import { ArrowLeftIcon, PencilIcon } from '@heroicons/react/24/outline';
import { loanAPI, formatCurrency, formatDate } from '../../services/api';
import LoadingSpinner from '../../components/UI/LoadingSpinner';
import PrintableReceipt from '../../components/Reports/PrintableReceipt';

const LoanDetails = () => {
  const { id } = useParams();
  // Hooks must be declared unconditionally at top-level
  const [activeTab, setActiveTab] = useState('overview');
  const [paymentsPage, setPaymentsPage] = useState(1);
  const [interestPage, setInterestPage] = useState(1);
  const [pageSize, setPageSize] = useState(5);
  const [paymentSearch, setPaymentSearch] = useState('');

  const { data, isLoading, error } = useQuery(
    ['loan', id],
    () => loanAPI.getLoan(id),
    {
      enabled: !!id,
    }
  );

  const apiData = data?.data?.data || null;
  const { loan, items, payments, interestCalculations, outstandingAmount } = apiData || {};

  // Calculate upcoming monthly interest for next month
  const upcomingMonthlyInterest = useMemo(() => {
    if (!loan || !loan.loan_amount || !loan.interest_rate) return 0;
    const loanAmount = parseFloat(loan.loan_amount) || 0;
    const interestRate = parseFloat(loan.interest_rate) || 0;
    return Number((loanAmount * (interestRate / 100)).toFixed(2));
  }, [loan]);

  // Build monthly interest ledger with paid/unpaid status
  const monthlyLedger = useMemo(() => {
    if (!Array.isArray(interestCalculations)) return [];
    const monthsAll = [...interestCalculations].sort((a, b) => new Date(a.calculation_date) - new Date(b.calculation_date));

    // Only include months whose due date (same day as loan start) has passed as of today
    const loanStart = loan ? new Date(loan.loan_date) : null;
    const dueDay = loanStart ? loanStart.getDate() : 1;
    const today = new Date();
    const months = monthsAll.filter((m) => {
      const d = new Date(m.calculation_date);
      const monthDue = new Date(d.getFullYear(), d.getMonth(), dueDay);
      return monthDue <= today;
    });
    const paymentsSortedAll = [...(payments || [])].sort((a, b) => new Date(a.payment_date) - new Date(b.payment_date));

    // Build an explicit set of months marked as paid via months_selected in payments
    const explicitlyPaidMonths = new Set();
    let latestFullSettlementDate = null;
    for (const p of paymentsSortedAll) {
      try {
        const sel = Array.isArray(p.months_selected) ? p.months_selected : JSON.parse(p.months_selected || '[]');
        if (Array.isArray(sel)) sel.forEach((mk) => explicitlyPaidMonths.add(String(mk)));
      } catch (_) {
        // ignore parse errors
      }
      if (String(p.payment_type) === 'full_settlement') {
        const d = new Date(p.payment_date);
        if (!latestFullSettlementDate || d > latestFullSettlementDate) latestFullSettlementDate = d;
      }
    }

    // Exclude explicit-month payments from the pooled allocation to avoid double counting
    const paymentsSorted = paymentsSortedAll.filter((p) => {
      try {
        const sel = Array.isArray(p.months_selected) ? p.months_selected : JSON.parse(p.months_selected || '[]');
        return !(Array.isArray(sel) && sel.length > 0);
      } catch (_) {
        return true;
      }
    });

    // Build cumulative interest payment pool over time
    let paidPool = 0;
    let payIdx = 0;

    return months.map((m) => {
      const mDate = new Date(m.calculation_date);
      // Add all payments up to and including this month to the pool
      while (
        payIdx < paymentsSorted.length &&
        new Date(paymentsSorted[payIdx].payment_date) <= new Date(mDate.getFullYear(), mDate.getMonth() + 1, 0)
      ) {
        paidPool += parseFloat(paymentsSorted[payIdx].interest_amount || 0);
        payIdx += 1;
      }

      const monthlyInterest = parseFloat(m.interest_amount || 0);
      const monthKey = `${mDate.getFullYear()}-${String(mDate.getMonth() + 1).padStart(2, '0')}`;
      let allocated = 0;
      let remaining = monthlyInterest;

      const isExplicit = explicitlyPaidMonths.has(monthKey) || (latestFullSettlementDate && new Date(mDate.getFullYear(), mDate.getMonth() + 1, 0) <= latestFullSettlementDate);
      if (isExplicit) {
        // Mark fully paid, but do NOT subtract from pooled payments
        allocated = monthlyInterest;
      } else {
        allocated = Math.min(monthlyInterest, paidPool);
        paidPool -= allocated;
        if (paidPool < 0) paidPool = 0;
      }
      remaining = Number((monthlyInterest - allocated).toFixed(2));
      return {
        monthKey,
        calculation_date: m.calculation_date,
        interest: Number(monthlyInterest.toFixed(2)),
        paid: Number(allocated.toFixed(2)),
        remaining,
        status: remaining <= 0 ? 'Paid' : 'Unpaid',
      };
    });
  }, [interestCalculations, payments]);

  // Derived paging and filtering data
  const filteredPayments = useMemo(() => {
    const list = Array.isArray(payments) ? payments : [];
    if (!paymentSearch) return list;
    const q = paymentSearch.toLowerCase();
    return list.filter((p) =>
      String(p.payment_number || '').toLowerCase().includes(q) ||
      String(p.payment_type || '').toLowerCase().includes(q)
    );
  }, [payments, paymentSearch]);

  const paymentsTotalPages = Math.max(1, Math.ceil((filteredPayments?.length || 0) / pageSize));
  const paymentsPageData = useMemo(() => {
    const start = (paymentsPage - 1) * pageSize;
    return filteredPayments.slice(start, start + pageSize);
  }, [filteredPayments, paymentsPage, pageSize]);

  const interestTotalPages = Math.max(1, Math.ceil((monthlyLedger?.length || 0) / pageSize));
  const interestPageData = useMemo(() => {
    const start = (interestPage - 1) * pageSize;
    return monthlyLedger.slice(start, start + pageSize);
  }, [monthlyLedger, interestPage, pageSize]);

  if (isLoading) {
    return <LoadingSpinner size="lg" />;
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600">Error loading loan: {error.message}</p>
      </div>
    );
  }

  if (!apiData) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Loan not found</p>
      </div>
    );
  }

  // Tabs to avoid page scroll
  const tabs = [
    { key: 'overview', label: 'Overview' },
    { key: 'items', label: `Pledged Items${items && items.length ? ` (${items.length})` : ''}` },
    { key: 'payments', label: `Payment History${payments && payments.length ? ` (${payments.length})` : ''}` },
    { key: 'interest', label: `Monthly Interest${monthlyLedger && monthlyLedger.length ? ` (${monthlyLedger.length})` : ''}` }
  ];

  return (
    <div className="h-auto flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Link
            to="/loans"
            className="inline-flex items-center text-sm font-medium text-gray-500 hover:text-gray-700"
          >
            <ArrowLeftIcon className="h-4 w-4 mr-1" />
            Back to Loans
          </Link>
        </div>
        <div className="flex space-x-3">
          <Link
            to={`/loans/${id}/edit`}
            className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-primary-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            <PencilIcon className="h-4 w-4 mr-2" />
            Edit
          </Link>
          <a href="#receipt" className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-primary-50">Customer Copy</a>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white shadow sm:rounded-lg flex-1 flex flex-col overflow-hidden">
        <div className="px-4 pt-4 sm:px-6">
          <h1 className="text-2xl font-bold text-gray-900">{loan?.loan_number || 'Loan Details'}</h1>
        </div>
        <div className="px-4 sm:px-6 mt-3 border-b sticky top-0 bg-white z-10">
          <nav className="-mb-px flex flex-wrap gap-2" aria-label="Tabs">
            {tabs.map((t) => (
              <button
                key={t.key}
                onClick={() => setActiveTab(t.key)}
                className={`${activeTab === t.key ? 'border-primary-600 text-primary-700' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} whitespace-nowrap py-2 px-3 border-b-2 text-sm font-medium rounded-t`}
              >
                {t.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Content: fills remaining height with internal scroll */}
        <div className="px-4 sm:px-6 py-3 flex-1 overflow-hidden">
          <div className="h-full overflow-auto">
            {activeTab === 'overview' && (
            <>
            {/* Overview compact table */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
              <div className="p-3 bg-primary-50 rounded border">
                <div className="text-xs text-gray-500">Customer</div>
                <div className="text-sm text-gray-900">
                  <Link to={`/customers/${loan.customer_id}`} className="text-primary-600 hover:text-primary-900">{loan.first_name} {loan.last_name}</Link>
                  <div className="text-gray-500">{loan.mobile_1}</div>
                </div>
              </div>
              <div className="p-3 bg-primary-50 rounded border">
                <div className="text-xs text-gray-500">Status</div>
                <div className="text-sm font-semibold"><span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                  loan.status === 'active' ? 'bg-green-100 text-green-800' : loan.status === 'closed' ? 'bg-gray-100 text-gray-800' : 'bg-red-100 text-red-800'
                }`}>{loan.status}</span></div>
              </div>
              <div className="p-3 bg-white rounded border">
                <div className="text-xs text-gray-500">Loan Amount</div>
                <div className="text-sm font-semibold">{formatCurrency(loan.loan_amount)}</div>
              </div>
              <div className="p-3 bg-white rounded border">
                <div className="text-xs text-gray-500">Net Amount</div>
                <div className="text-sm font-semibold">{formatCurrency(loan.net_amount)}</div>
              </div>
              <div className="p-3 bg-white rounded border">
                <div className="text-xs text-gray-500">Outstanding Amount</div>
                <div className="text-sm font-semibold">{formatCurrency(outstandingAmount)}</div>
              </div>
              <div className="p-3 bg-white rounded border">
                <div className="text-xs text-gray-500">Interest Rate</div>
                <div className="text-sm font-semibold">{loan.interest_rate}% per month</div>
              </div>
              <div className="p-3 bg-white rounded border">
                <div className="text-xs text-gray-500">Processing Charge</div>
                <div className="text-sm font-semibold">{formatCurrency(loan.processing_charge)}</div>
              </div>
              <div className="p-3 bg-white rounded border">
                <div className="text-xs text-gray-500">LTV Ratio</div>
                <div className="text-sm font-semibold">{loan.loan_to_value_ratio}%</div>
              </div>
              <div className="p-3 bg-white rounded border">
                <div className="text-xs text-gray-500">Total Weight</div>
                <div className="text-sm font-semibold">{loan.total_weight} grams</div>
              </div>
              <div className="p-3 bg-white rounded border">
                <div className="text-xs text-gray-500">Market Value</div>
                <div className="text-sm font-semibold">{formatCurrency(loan.market_value)}</div>
              </div>
              <div className="p-3 bg-white rounded border">
                <div className="text-xs text-gray-500">Loan Date</div>
                <div className="text-sm font-semibold">{formatDate(loan.loan_date)}</div>
              </div>
              <div className="p-3 bg-white rounded border">
                <div className="text-xs text-gray-500">Maturity Date</div>
                <div className="text-sm font-semibold">{formatDate(loan.maturity_date)}</div>
              </div>
            </div>

            <dl className="mb-4 hidden">
            <div className="bg-primary-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500">Customer</dt>
              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                <Link
                  to={`/customers/${loan.customer_id}`}
                  className="text-primary-600 hover:text-primary-900"
                >
                  {loan.first_name} {loan.last_name}
                </Link>
                <div className="text-gray-500">{loan.mobile_1}</div>
              </dd>
            </div>
            <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500">Loan Amount</dt>
              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                {formatCurrency(loan.loan_amount)}
              </dd>
            </div>
            <div className="bg-primary-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500">Interest Rate</dt>
              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                {loan.interest_rate}% per month
              </dd>
            </div>
            <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500">Processing Charge</dt>
              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                {formatCurrency(loan.processing_charge)}
              </dd>
            </div>
            <div className="bg-primary-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500">Net Amount</dt>
              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                {formatCurrency(loan.net_amount)}
              </dd>
            </div>
            <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500">Outstanding Amount</dt>
              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                {formatCurrency(outstandingAmount)}
              </dd>
            </div>
            <div className="bg-primary-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500">Total Weight</dt>
              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                {loan.total_weight} grams
              </dd>
            </div>
            <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500">Market Value</dt>
              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                {formatCurrency(loan.market_value)}
              </dd>
            </div>
            <div className="bg-primary-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500">LTV Ratio</dt>
              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                {loan.loan_to_value_ratio}%
              </dd>
            </div>
            <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500">Loan Date</dt>
              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                {formatDate(loan.loan_date)}
              </dd>
            </div>
            <div className="bg-primary-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500">Maturity Date</dt>
              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                {formatDate(loan.maturity_date)}
              </dd>
            </div>
            <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500">Status</dt>
              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  loan.status === 'active' 
                    ? 'bg-green-100 text-green-800'
                    : loan.status === 'closed'
                    ? 'bg-gray-100 text-gray-800'
                    : 'bg-red-100 text-red-800'
                }`}>
                  {loan.status}
                </span>
              </dd>
            </div>
            </dl>
            </>
            )}

            {activeTab === 'items' && (
              <>
                {items && items.length > 0 ? (
                  <div className="border border-gray-200 sm:rounded">
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-primary-50">
                          <tr>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Item</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Category</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Weight</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Purity</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Rate/Gram</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Value</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {items.map((item) => (
                            <tr key={item.id}>
                              <td className="px-4 py-2 whitespace-nowrap text-sm font-medium text-gray-900">{item.item_name}</td>
                              <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-500">{item.category_name}</td>
                              <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">{item.weight} g</td>
                              <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">{item.purity}</td>
                              <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">{formatCurrency(item.rate_per_gram)}</td>
                              <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">{formatCurrency(item.value)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-12 bg-gray-50 rounded-lg border border-gray-200">
                    <svg className="w-12 h-12 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                    </svg>
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No Pledged Items</h3>
                    <p className="text-gray-500 mb-4">
                      {loan?.enforce_ltv === false 
                        ? "This loan was created without LTV enforcement, so pledged items are optional."
                        : "No pledged items found for this loan."
                      }
                    </p>
                    {loan?.enforce_ltv === false && (
                      <div className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-blue-100 text-blue-800">
                        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        LTV Not Enforced
                      </div>
                    )}
                  </div>
                )}
              </>
            )}

            {activeTab === 'payments' && (
              <>
                {payments && payments.length > 0 ? (
                  <div className="border border-gray-200 sm:rounded">
                    <div className="flex items-center justify-between p-2">
                      <input className="input h-8 text-sm" placeholder="Search payments..." value={paymentSearch} onChange={(e)=>{ setPaymentSearch(e.target.value); setPaymentsPage(1); }} />
                      <div className="flex items-center space-x-2 text-sm">
                        <span>Rows:</span>
                        <select className="input h-8 text-sm" value={pageSize} onChange={(e)=>{ setPageSize(parseInt(e.target.value,10)||5); setPaymentsPage(1); setInterestPage(1); }}>
                          {[5,8,10,15,20].map(n=> <option key={n} value={n}>{n}</option>)}
                        </select>
                      </div>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-primary-50">
                          <tr>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">#</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Principal</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Interest</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {paymentsPageData.map((payment) => (
                            <tr key={payment.id}>
                              <td className="px-4 py-2 whitespace-nowrap text-sm font-medium text-gray-900">{payment.payment_number}</td>
                              <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-500">{formatDate(payment.payment_date)}</td>
                          <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900"><span className="capitalize">{(payment.payment_type || '').replace('_', ' ')}</span></td>
                              <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">{formatCurrency(payment.amount)}</td>
                              <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">{formatCurrency(payment.principal_amount)}</td>
                              <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">{formatCurrency(payment.interest_amount)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <div className="flex items-center justify-between p-2 text-sm">
                      <div>Page {paymentsPage} of {paymentsTotalPages}</div>
                      <div className="space-x-2">
                        <button className="px-2 py-1 border rounded disabled:opacity-50" onClick={()=>setPaymentsPage((p)=>Math.max(1,p-1))} disabled={paymentsPage<=1}>Prev</button>
                        <button className="px-2 py-1 border rounded disabled:opacity-50" onClick={()=>setPaymentsPage((p)=>Math.min(paymentsTotalPages,p+1))} disabled={paymentsPage>=paymentsTotalPages}>Next</button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-12 bg-gray-50 rounded-lg border border-gray-200">
                    <svg className="w-12 h-12 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No Payment History Found</h3>
                    <p className="text-gray-500 mb-4">
                      No payments have been made for this loan yet.
                    </p>
                    <div className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-yellow-100 text-yellow-800">
                      <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      Awaiting First Payment
                    </div>
                  </div>
                )}
              </>
            )}

            {activeTab === 'interest' && (
              <>
                {/* Upcoming Monthly Interest Card */}
                <div className="mb-6 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-800">Upcoming Monthly Interest</h3>
                      <p className="text-sm text-gray-600">Next month's interest amount</p>
                    </div>
                    <div className="text-right">
                      <div className="text-2xl font-bold text-blue-600">{formatCurrency(upcomingMonthlyInterest)}</div>
                      <div className="text-sm text-gray-500">Due next month</div>
                    </div>
                  </div>
                </div>

                {monthlyLedger && monthlyLedger.length > 0 ? (
                  <div className="border border-gray-200 sm:rounded">
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-primary-50">
                          <tr>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Month</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Interest</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Paid</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Remaining</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {interestPageData.map((row) => (
                            <tr key={row.monthKey}>
                              <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">{row.monthKey}</td>
                              <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">{formatCurrency(row.interest)}</td>
                              <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">{formatCurrency(row.paid)}</td>
                              <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">{formatCurrency(row.remaining)}</td>
                              <td className="px-4 py-2 whitespace-nowrap text-sm"><span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${row.status === 'Paid' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>{row.status}</span></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <div className="flex items-center justify-between p-2 text-sm">
                      <div>Page {interestPage} of {interestTotalPages}</div>
                      <div className="space-x-2">
                        <button className="px-2 py-1 border rounded disabled:opacity-50" onClick={()=>setInterestPage((p)=>Math.max(1,p-1))} disabled={interestPage<=1}>Prev</button>
                        <button className="px-2 py-1 border rounded disabled:opacity-50" onClick={()=>setInterestPage((p)=>Math.min(interestTotalPages,p+1))} disabled={interestPage>=interestTotalPages}>Next</button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-12 bg-gray-50 rounded-lg border border-gray-200">
                    <svg className="w-12 h-12 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                    </svg>
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No Interest History</h3>
                    <p className="text-gray-500 mb-4">
                      No monthly interest calculations have been generated yet.
                    </p>
                    <div className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-blue-100 text-blue-800">
                      <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                      </svg>
                      Interest Will Be Calculated Monthly
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Pledged Items */}
      {items && items.length > 0 && (
        <div className="bg-white shadow overflow-hidden sm:rounded-lg">
          <div className="px-4 py-5 sm:px-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900">Pledged Items</h3>
            <p className="mt-1 max-w-2xl text-sm text-gray-500">
              Items pledged for this loan
            </p>
          </div>
          <div className="border-t border-gray-200">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-primary-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Item
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Category
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Weight
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Purity
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Rate/Gram
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Value
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {items.map((item) => (
                    <tr key={item.id}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {item.item_name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {item.category_name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {item.weight} g
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {item.purity}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatCurrency(item.rate_per_gram)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatCurrency(item.value)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Printable Receipt */}
      <div id="receipt" className="mt-6">
        <PrintableReceipt
          id={`loan-receipt-${loan?.id}`}
          receipt={{ number: loan?.loan_number, date: loan?.loan_date, loanNumber: loan?.loan_number }}
          customer={{
            name: `${loan?.first_name || ''} ${loan?.last_name || ''}`.trim(),
            fatherOrSpouse: loan?.father_name || loan?.guardian_name || '',
            address: `${loan?.address_line_1 || ''} ${loan?.address_line_2 || ''} ${loan?.city || ''} ${loan?.state || ''} ${loan?.pincode || ''}`.trim(),
            mobile1: loan?.mobile_1,
            mobile2: loan?.mobile_2,
            aadhar: loan?.aadhar_number,
            code: loan?.customer_code,
          }}
          loan={{
            amount: loan?.loan_amount,
            interestRate: typeof loan?.interest_rate === 'number' ? loan?.interest_rate : undefined,
            agreement: loan?.remarks,
            itemsText: (items || []).map(i=>`${i.item_name} ${i.weight}g ${i.purity}`).join(', '),
          }}
          meta={{ paidMonths: [] }}
        />
      </div>

      {/* Payment History */}
      {payments && payments.length > 0 && (
        <div className="bg-white shadow overflow-hidden sm:rounded-lg">
          <div className="px-4 py-5 sm:px-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900">Payment History</h3>
            <p className="mt-1 max-w-2xl text-sm text-gray-500">
              All payments made for this loan
            </p>
          </div>
          <div className="border-t border-gray-200">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-primary-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Payment #
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Type
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Amount
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Principal
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Interest
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {payments.map((payment) => (
                    <tr key={payment.id}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {payment.payment_number}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(payment.payment_date)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <span className="capitalize">
                          {(payment.payment_type || '').replace('_', ' ')}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatCurrency(payment.amount)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatCurrency(payment.principal_amount)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatCurrency(payment.interest_amount)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Monthly Interest Ledger */}
      {/* {monthlyLedger && monthlyLedger.length > 0 && (
        <div className="bg-white shadow overflow-hidden sm:rounded-lg">
          <div className="px-4 py-5 sm:px-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900">Monthly Interest</h3>
            <p className="mt-1 max-w-2xl text-sm text-gray-500">Monthly interest breakdown (read-only)</p>
          </div>
          <div className="border-t border-gray-200">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-primary-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Month</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Interest</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Paid</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Remaining</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {monthlyLedger.map((row) => (
                    <tr key={row.monthKey}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{row.monthKey}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{formatCurrency(row.interest)}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{formatCurrency(row.paid)}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{formatCurrency(row.remaining)}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${row.status === 'Paid' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                          {row.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )} */}
    </div>
  );
};

export default LoanDetails;
